import { useEffect, useState } from "react";

const BELT_SEQUENCE = [
  { nome: "Branca", cor: "#FFFFFF", border: "#D1D5DB" },
  { nome: "Azul", cor: "#3B82F6", border: "#2563EB" },
  { nome: "Roxa", cor: "#7C3AED", border: "#6D28D9" },
  { nome: "Marrom", cor: "#92400E", border: "#78350F" },
  { nome: "Preta", cor: "#1F2937", border: "#111827" },
];

interface BeltLoaderProps {
  message?: string;
}

export default function BeltLoader({ message = "Carregando..." }: BeltLoaderProps) {
  const [beltIndex, setBeltIndex] = useState(0);
  const [grauCount, setGrauCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setGrauCount((prev) => {
        if (prev >= 4) {
          setBeltIndex((bi) => (bi + 1) % BELT_SEQUENCE.length);
          return 0;
        }
        return prev + 1;
      });
    }, 400);
    return () => clearInterval(interval);
  }, []);

  const belt = BELT_SEQUENCE[beltIndex];
  const isPreta = belt.nome === "Preta";
  const grauColor = isPreta ? "#DC2626" : "#FFFFFF";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6">
      {/* Belt SVG */}
      <div className="relative animate-scale-in">
        <svg
          width="280"
          height="56"
          viewBox="0 0 280 56"
          className="drop-shadow-lg"
          style={{ filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.15))" }}
        >
          {/* Belt body */}
          <rect
            x="2"
            y="8"
            width="276"
            height="40"
            rx="6"
            fill={belt.cor}
            stroke={belt.border}
            strokeWidth="2"
            className="transition-all duration-500"
          />

          {/* Belt texture lines */}
          <line x1="20" y1="18" x2="200" y2="18" stroke={belt.border} strokeWidth="0.5" opacity="0.3" />
          <line x1="20" y1="28" x2="200" y2="28" stroke={belt.border} strokeWidth="0.5" opacity="0.3" />
          <line x1="20" y1="38" x2="200" y2="38" stroke={belt.border} strokeWidth="0.5" opacity="0.3" />

          {/* Black tip (ponta preta) */}
          <rect
            x="210"
            y="8"
            width="68"
            height="40"
            rx="0"
            fill="#1F2937"
            className="transition-all duration-500"
          />
          {/* Rounded right end */}
          <rect
            x="268"
            y="8"
            width="12"
            height="40"
            rx="6"
            fill="#1F2937"
          />

          {/* Divider line between belt and tip */}
          <line x1="210" y1="8" x2="210" y2="48" stroke="#111827" strokeWidth="2" />

          {/* Grau stripes on the black tip */}
          {Array.from({ length: grauCount }).map((_, i) => {
            const stripeWidth = 6;
            const gap = 10;
            const totalWidth = 4 * stripeWidth + 3 * gap;
            const startX = 210 + (68 - totalWidth) / 2;
            const x = startX + i * (stripeWidth + gap);
            return (
              <rect
                key={i}
                x={x}
                y="14"
                width={stripeWidth}
                height="28"
                rx="1.5"
                fill={grauColor}
                className="animate-fade-in"
                style={{ animationDuration: "0.25s" }}
              />
            );
          })}

          {/* Belt knot/fold in the middle */}
          <rect x="90" y="4" width="30" height="48" rx="4" fill={belt.cor} stroke={belt.border} strokeWidth="1.5" opacity="0.7" />
          <rect x="96" y="2" width="18" height="52" rx="3" fill={belt.cor} stroke={belt.border} strokeWidth="1.5" opacity="0.5" />
        </svg>

        {/* Shimmer overlay */}
        <div
          className="absolute inset-0 overflow-hidden rounded-md pointer-events-none"
          style={{ mixBlendMode: "overlay" }}
        >
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)",
              animation: "shimmer 2s ease-in-out infinite",
            }}
          />
        </div>
      </div>

      {/* Belt name */}
      <div className="flex flex-col items-center gap-2 animate-fade-in">
        <span className="text-sm font-semibold text-foreground tracking-wide">
          {belt.nome}
          {grauCount > 0 && (
            <span className="text-muted-foreground font-normal ml-1">
              — {grauCount}º grau
            </span>
          )}
        </span>
        <span className="text-sm text-muted-foreground">{message}</span>
      </div>

      {/* Progress dots */}
      <div className="flex gap-2 mt-1">
        {BELT_SEQUENCE.map((b, i) => (
          <div
            key={b.nome}
            className="w-2.5 h-2.5 rounded-full border transition-all duration-300"
            style={{
              backgroundColor: i <= beltIndex ? b.cor : "transparent",
              borderColor: b.border,
              transform: i === beltIndex ? "scale(1.3)" : "scale(1)",
            }}
          />
        ))}
      </div>

      {/* CSS for shimmer */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}
